import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Shield, Loader2, UserCheck, Globe, Mail, KeyRound, CreditCard, Clock, Activity } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { api, getApiErrorMessage } from '@/lib/api'
import { toast } from 'sonner'
import { format } from 'date-fns'

// CHECKPOINT: PRD-01 FR-1.9.2 — Full account drill-down response shapes
interface Account {
  id: string
  email: string
  display_name: string | null
  plan: string
  status: string
  email_verified: boolean
  timezone: string | null
  current_period_start: string | null
  current_period_end: string | null
  last_active: string | null
  created_at: string
  updated_at: string
}

interface Subscription {
  id: string
  status: string
  plan: string
  amount_paid: number
  currency: string
  current_period_start: string | null
  current_period_end: string | null
  grace_period_end: string | null
  payment_failed_count: number
  created_at: string | null
}

interface Domain {
  id: string
  name: string
  status: string
  dns_verified_at: string | null
  created_at: string | null
}

interface Mailbox {
  id: string
  email: string
  status: string
  quota_mb: number
  last_login_at: string | null
  created_at: string | null
}

interface ApiKey {
  id: string
  name: string
  key_prefix: string
  scopes: string[]
  revoked_at: string | null
  revoked_reason: string | null
  last_used_at: string | null
  created_at: string | null
}

interface AuditEvent {
  id: string
  action: string
  superadmin_id: string
  details: Record<string, unknown> | null
  created_at: string
}

interface AccountDetailResponse {
  account: Account
  subscription: Subscription | null
  domains: Domain[]
  mailboxes: Mailbox[]
  api_keys: ApiKey[]
  recent_activity: AuditEvent[]
}

// CHECKPOINT: PRD-10 FR-10.12.2 — Account detail with drill-down tabs
export function AccountDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [data, setData] = useState<AccountDetailResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [impersonating, setImpersonating] = useState(false)

  useEffect(() => {
    const fetchAccount = async () => {
      if (!id) return
      try {
        const res = await api.get(`/admin/accounts/${id}`)
        setData(res.data)
      } catch {
        toast.error('Failed to load account')
      } finally {
        setLoading(false)
      }
    }
    fetchAccount()
  }, [id])

  // CHECKPOINT: PRD-01 FR-1.9.2 — Open dashboard impersonation URL returned by the backend
  const handleImpersonate = async () => {
    if (!id) return
    setImpersonating(true)
    try {
      const res = await api.post(`/admin/impersonate/${id}`)
      const dashboardUrl = res.data.dashboard_url
      if (dashboardUrl) {
        window.open(dashboardUrl, '_blank', 'noopener,noreferrer')
        toast.success('Dashboard opened in new tab')
      } else {
        toast.error('Impersonation URL missing')
      }
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'Failed to impersonate'))
    } finally {
      setImpersonating(false)
    }
  }

  const formatDate = (value: string | null) => {
    if (!value) return '—'
    try {
      return format(new Date(value), 'MMM d, yyyy HH:mm')
    } catch {
      return value
    }
  }

  const statusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'verified':
        return 'bg-emerald-500/10 text-emerald-500'
      case 'suspended':
      case 'banned':
        return 'bg-red-500/10 text-red-500'
      case 'pending':
      case 'unverified':
        return 'bg-amber-500/10 text-amber-500'
      default:
        return 'bg-gray-500/10 text-gray-500'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Account not found</p>
        <Button variant="link" onClick={() => navigate('/accounts')}>
          Back to accounts
        </Button>
      </div>
    )
  }

  const { account, subscription, domains, mailboxes, api_keys, recent_activity } = data

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/accounts')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Account Detail
            </h1>
            <p className="text-sm text-muted-foreground">{account.email}</p>
          </div>
        </div>
        <Button variant="secondary" onClick={handleImpersonate} disabled={impersonating}>
          {impersonating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserCheck className="h-4 w-4 mr-2" />}
          Impersonate
        </Button>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview"><Activity className="h-4 w-4 mr-2" /> Overview</TabsTrigger>
          <TabsTrigger value="domains"><Globe className="h-4 w-4 mr-2" /> Domains</TabsTrigger>
          <TabsTrigger value="mailboxes"><Mail className="h-4 w-4 mr-2" /> Mailboxes</TabsTrigger>
          <TabsTrigger value="apikeys"><KeyRound className="h-4 w-4 mr-2" /> API Keys</TabsTrigger>
          <TabsTrigger value="subscription"><CreditCard className="h-4 w-4 mr-2" /> Subscription</TabsTrigger>
          <TabsTrigger value="activity"><Clock className="h-4 w-4 mr-2" /> Activity</TabsTrigger>
        </TabsList>

        {/* CHECKPOINT: PRD-10 FR-10.12.2 — Overview tab */}
        <TabsContent value="overview">
          <Card>
            <CardHeader>
              <CardTitle>Account Overview</CardTitle>
              <CardDescription>Core account metadata and status.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Display Name</p>
                  <p className="font-medium">{account.display_name || '—'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <p className="font-medium">{account.email}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Plan</p>
                  <p className="font-medium capitalize">{account.plan}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <div className="mt-1">
                    <Badge variant="secondary" className={statusColor(account.status)}>
                      {account.status}
                    </Badge>
                  </div>
                </div>
                <div>
                  <p className="text-muted-foreground">Email Verified</p>
                  <p className="font-medium">{account.email_verified ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Timezone</p>
                  <p className="font-medium">{account.timezone || 'UTC'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Last Active</p>
                  <p className="font-medium">{formatDate(account.last_active)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Current Period</p>
                  <p className="font-medium">
                    {formatDate(account.current_period_start)} → {formatDate(account.current_period_end)}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Created</p>
                  <p className="font-medium">{formatDate(account.created_at)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Updated</p>
                  <p className="font-medium">{formatDate(account.updated_at)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CHECKPOINT: PRD-10 FR-10.12.2 — Domains tab */}
        <TabsContent value="domains">
          <Card>
            <CardHeader>
              <CardTitle>Domains ({domains.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {domains.length === 0 ? (
                <p className="text-sm text-muted-foreground">No domains.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>DNS Verified</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {domains.map((domain) => (
                      <TableRow key={domain.id}>
                        <TableCell className="font-medium">{domain.name}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className={statusColor(domain.status)}>
                            {domain.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(domain.dns_verified_at)}</TableCell>
                        <TableCell>{formatDate(domain.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CHECKPOINT: PRD-10 FR-10.12.2 — Mailboxes tab */}
        <TabsContent value="mailboxes">
          <Card>
            <CardHeader>
              <CardTitle>Mailboxes ({mailboxes.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {mailboxes.length === 0 ? (
                <p className="text-sm text-muted-foreground">No mailboxes.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Quota (MB)</TableHead>
                      <TableHead>Last Login</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {mailboxes.map((mailbox) => (
                      <TableRow key={mailbox.id}>
                        <TableCell className="font-medium">{mailbox.email}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className={statusColor(mailbox.status)}>
                            {mailbox.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{mailbox.quota_mb}</TableCell>
                        <TableCell>{formatDate(mailbox.last_login_at)}</TableCell>
                        <TableCell>{formatDate(mailbox.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CHECKPOINT: PRD-10 FR-10.12.2 — API Keys tab */}
        <TabsContent value="apikeys">
          <Card>
            <CardHeader>
              <CardTitle>API Keys ({api_keys.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {api_keys.length === 0 ? (
                <p className="text-sm text-muted-foreground">No API keys.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Prefix</TableHead>
                      <TableHead>Scopes</TableHead>
                      <TableHead>Last Used</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {api_keys.map((key) => (
                      <TableRow key={key.id}>
                        <TableCell className="font-medium">{key.name}</TableCell>
                        <TableCell><code className="text-xs">{key.key_prefix}</code></TableCell>
                        <TableCell>{(key.scopes || []).join(', ')}</TableCell>
                        <TableCell>{formatDate(key.last_used_at)}</TableCell>
                        <TableCell>{formatDate(key.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CHECKPOINT: PRD-10 FR-10.12.2 — Subscription tab */}
        <TabsContent value="subscription">
          <Card>
            <CardHeader>
              <CardTitle>Subscription</CardTitle>
            </CardHeader>
            <CardContent>
              {!subscription ? (
                <p className="text-sm text-muted-foreground">No subscription record.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Plan</p>
                    <p className="font-medium capitalize">{subscription.plan}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Status</p>
                    <div className="mt-1">
                      <Badge variant="secondary" className={statusColor(subscription.status)}>
                        {subscription.status}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Amount Paid</p>
                    <p className="font-medium">
                      {subscription.amount_paid} {subscription.currency.toUpperCase()}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Payment Failures</p>
                    <p className="font-medium">{subscription.payment_failed_count}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Current Period</p>
                    <p className="font-medium">
                      {formatDate(subscription.current_period_start)} → {formatDate(subscription.current_period_end)}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Grace Period End</p>
                    <p className="font-medium">{formatDate(subscription.grace_period_end)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Created</p>
                    <p className="font-medium">{formatDate(subscription.created_at)}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CHECKPOINT: PRD-10 FR-10.12.2 — Recent activity tab */}
        <TabsContent value="activity">
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity ({recent_activity.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {recent_activity.length === 0 ? (
                <p className="text-sm text-muted-foreground">No recent activity.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Action</TableHead>
                      <TableHead>Admin ID</TableHead>
                      <TableHead>Details</TableHead>
                      <TableHead>Timestamp</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recent_activity.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell className="font-medium">{event.action}</TableCell>
                        <TableCell><code className="text-xs">{event.superadmin_id}</code></TableCell>
                        <TableCell className="max-w-xs truncate">
                          {event.details ? JSON.stringify(event.details) : '—'}
                        </TableCell>
                        <TableCell>{formatDate(event.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
